import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { skillsApi } from '../../utils/api';
import WizardLayout from '../../components/builder/WizardLayout';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { Sparkles, ChevronDown, ChevronUp, Loader, RotateCcw } from 'lucide-react';
import ComparisonModal from '../../components/builder/ComparisonModal';
import { toast } from 'sonner';

const Step1PurposeEnhanced = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [enhancing, setEnhancing] = useState(false);
  const [detectingCategory, setDetectingCategory] = useState(false);
  const [showGuidance, setShowGuidance] = useState(false);
  const [showComparison, setShowComparison] = useState(false);
  const [enhancedDescription, setEnhancedDescription] = useState('');
  const [originalDescription, setOriginalDescription] = useState('');
  const [hasEnhanced, setHasEnhanced] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    category_id: ''
  });
  const [suggestedCategories, setSuggestedCategories] = useState([]);

  const categories = [
    { value: 'writing-content', label: '✍️ Writing & Content', group: 'Popular' },
    { value: 'business-productivity', label: '💼 Business & Productivity', group: 'Popular' },
    { value: 'marketing-sales', label: '📊 Marketing & Sales', group: 'Popular' },
    { value: 'education-learning', label: '🎓 Education & Learning', group: 'Popular' },
    { value: 'creative-design', label: '🎨 Creative & Design', group: 'Creative' },
    { value: 'skills-workflows', label: '⚡ Skills & Workflows', group: 'Creative' },
    { value: 'agents-ai', label: '🤖 Agents & AI', group: 'Creative' },
    { value: 'developer-tools', label: '💻 Developer Tools', group: 'Creative' },
    { value: 'data-analytics', label: '📈 Data & Analytics', group: 'Data' },
    { value: 'documents-files', label: '📄 Documents & Files', group: 'Data' },
    { value: 'research-knowledge', label: '🔬 Research & Knowledge', group: 'Data' },
    { value: 'web-apps', label: '🌐 Web & Apps', group: 'Data' },
    { value: 'security-compliance', label: '🔒 Security & Compliance', group: 'Advanced' },
    { value: 'devops-infrastructure', label: '⚙️ DevOps & Infrastructure', group: 'Advanced' },
    { value: 'science-research', label: '🧪 Science & Research', group: 'Advanced' },
    { value: 'custom-experimental', label: '🚀 Custom & Experimental', group: 'Advanced' }
  ];

  useEffect(() => {
    // Check for Discovery Session data FIRST
    const discoveryData = localStorage.getItem('DISCOVERY_DATA');
    if (discoveryData) {
      console.log('✅ WIZARD: Found discovery data in localStorage');
      try {
        const data = JSON.parse(discoveryData);
        console.log('✅ WIZARD: Parsed data:', data);
        
        const newFormData = {
          name: data.name || '',
          slug: (data.name || '').toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''),
          description: data.description || '',
          category_id: data.category ? 
            categories.find(c => c.label.toLowerCase().includes(data.category.toLowerCase()))?.value || '' 
            : ''
        };
        
        console.log('✅ WIZARD: Setting form data:', newFormData);
        setFormData(newFormData);
        
        localStorage.removeItem('DISCOVERY_DATA');
        console.log('✅ WIZARD: Data loaded successfully, localStorage cleared');
        
      } catch (error) {
        console.error('❌ WIZARD: Parse error:', error);
      }
      return;
    }
    
    console.log('ℹ️ WIZARD: No discovery data found in localStorage');
    
    // Check if coming from Skill Idea Assistant
    const urlParams = new URLSearchParams(window.location.search);
    const fromAssistant = urlParams.get('from') === 'assistant';
    
    if (fromAssistant) {
      const skillSpecJson = sessionStorage.getItem('skillSpec');
      if (skillSpecJson) {
        try {
          const skillSpec = JSON.parse(skillSpecJson);
          setFormData({
            name: skillSpec.skill_name || '',
            slug: skillSpec.slug || '',
            description: skillSpec.description || '',
            category_id: skillSpec.category ? 
              categories.find(c => c.label.includes(skillSpec.category))?.value || '' 
              : ''
          });
          
          // Clear from session storage
          sessionStorage.removeItem('skillSpec');
          
          // Show success message
          toast.success('💡 Skill concept loaded from Idea Assistant!', {
            description: 'Your skill details have been pre-filled. Review and continue when ready.'
          });
        } catch (error) {
          console.error('Failed to parse skill spec:', error);
        }
      }
    } else if (id && id !== 'new') {
      loadSkill();
    }
  }, [id]);

  // Auto-generate slug from name
  useEffect(() => {
    if (formData.name && !formData.slug) {
      const slug = formData.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .substring(0, 50);
      setFormData(prev => ({ ...prev, slug }));
    }
  }, [formData.name]);

  // Auto-detect category when description reaches 50 chars
  useEffect(() => {
    if (formData.description.length >= 50 && !formData.category_id && !detectingCategory) {
      detectCategory();
    }
  }, [formData.description]);

  const loadSkill = async () => {
    try {
      const response = await skillsApi.get(id);
      const skill = response.data;
      setFormData({
        name: skill.name || '',
        slug: skill.slug || '',
        description: skill.description || '',
        category_id: skill.category_id || ''
      });
    } catch (error) {
      console.error('Failed to load skill', error);
      toast.error('Failed to load skill');
    }
  };

  const detectCategory = async () => {
    setDetectingCategory(true);
    try {
      const { data } = await skillsApi.detectCategory({
        description: formData.description
      });

      if (data.confidence > 0.8) {
        // High confidence - auto-select
        setFormData(prev => ({ ...prev, category_id: data.category }));
        toast.success('Category auto-detected!');
      } else if (data.confidence > 0.5) {
        // Medium confidence - show suggestions
        setSuggestedCategories([data.category]);
        toast.info('Category suggestion available');
      }
    } catch (error) {
      console.error('Failed to detect category', error);
      // Silently fail - not critical
    } finally {
      setDetectingCategory(false);
    }
  };

  const handleEnhanceDescription = async () => {
    if (formData.description.length < 50) {
      toast.error('Write at least 50 characters first');
      return;
    }

    // Store original before enhancing
    if (!originalDescription) {
      setOriginalDescription(formData.description);
    }

    setEnhancing(true);
    try {
      const { data } = await skillsApi.enhanceDescription({
        skillName: formData.name,
        description: formData.description
      });

      // Check if AI failed to structure properly
      if (data.error) {
        toast.error(data.error);
        return;
      }

      setEnhancedDescription(data.enhanced);
      setShowComparison(true);
    } catch (error) {
      console.error('Failed to enhance description', error);
      toast.error(error.response?.data?.detail || 'Failed to enhance description');
    } finally {
      setEnhancing(false);
    }
  };

  const handleUseEnhanced = () => {
    setFormData(prev => ({ ...prev, description: enhancedDescription }));
    setHasEnhanced(true);
    setShowComparison(false);
    toast.success('Enhanced description applied!');
  };

  const handleEditEnhanced = () => {
    setFormData(prev => ({ ...prev, description: enhancedDescription }));
    setHasEnhanced(true);
    setShowComparison(false);
    toast.info('You can now edit the enhanced description');
    // Focus textarea after a brief delay
    setTimeout(() => {
      document.getElementById('description')?.focus();
    }, 100);
  };

  const handleUseOriginal = () => {
    setShowComparison(false);
    toast.info('Keeping your original description');
  };

  const handleRevertToOriginal = () => {
    setFormData(prev => ({ ...prev, description: originalDescription }));
    setHasEnhanced(false);
    toast.success('Reverted to original description');
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const validateForm = () => {
    if (!formData.name || formData.name.length < 3) {
      toast.error('Skill name must be at least 3 characters');
      return false;
    }
    if (!formData.slug || formData.slug.length < 3) {
      toast.error('Slug must be at least 3 characters');
      return false;
    }
    if (!formData.description || formData.description.length < 10) {
      toast.error('Description must be at least 10 characters');
      return false;
    }
    if (!formData.category_id) {
      toast.error('Please select a category');
      return false;
    }
    return true;
  };

  const handleNext = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      let skillId = id;
      
      if (!id || id === 'new') {
        const response = await skillsApi.create({
          name: formData.name,
          slug: formData.slug,
          description: formData.description,
          category_id: formData.category_id,
          builder_step: 1,
          builder_data: {}
        });
        skillId = response.data.id;
      } else {
        await skillsApi.update(id, {
          name: formData.name,
          slug: formData.slug,
          description: formData.description,
          category_id: formData.category_id,
          builder_step: 1
        });
      }

      toast.success('Progress saved!');
      navigate(`/builder/${skillId}/step-2`);
    } catch (error) {
      console.error('Failed to save', error);
      toast.error(error.response?.data?.detail || 'Failed to save progress');
    } finally {
      setLoading(false);
    }
  };

  return (
    <WizardLayout
      currentStep={1}
      onNext={handleNext}
      nextLabel="Next: Generate with AI"
      nextDisabled={loading}
      showBack={false}
    >
      <div className="space-y-6">
        <div>
          <h2 className="text-3xl font-manrope font-bold text-text-dark dark:text-dark-text mb-2">
            What does your skill do?
          </h2>
          <p className="text-text-gray dark:text-dark-text-secondary">
            Describe your skill's purpose and let AI handle the rest
          </p>
        </div>

        <div className="space-y-6">
          {/* Skill Name */}
          <div className="space-y-2">
            <Label htmlFor="name" className="flex items-center gap-1">
              Skill Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g., Blog Post Generator"
              className="text-lg"
              maxLength={100}
            />
            <p className="text-xs text-text-gray dark:text-dark-text-secondary">
              {formData.name.length}/100 characters
            </p>
          </div>

          {/* Slug (Auto-generated, editable) */}
          <div className="space-y-2">
            <Label htmlFor="slug" className="flex items-center gap-1">
              File Name (Slug) <span className="text-red-500">*</span>
            </Label>
            <Input
              id="slug"
              name="slug"
              value={formData.slug}
              onChange={handleChange}
              placeholder="blog-post-generator"
              className="font-mono"
              maxLength={50}
            />
            <p className="text-xs text-text-gray dark:text-dark-text-secondary">
              {formData.slug.length}/50 characters • Auto-generated from name, editable
            </p>
          </div>

          {/* Expandable Guidance - NOW ABOVE DESCRIPTION */}
          <div className="border border-gray-200 dark:border-dark-border rounded-xl overflow-hidden">
            <button
              type="button"
              onClick={() => setShowGuidance(!showGuidance)}
              className="w-full px-4 py-3 flex justify-between items-center bg-gray-50 dark:bg-dark-bg hover:bg-gray-100 dark:hover:bg-dark-border transition-colors"
            >
              <span className="font-medium text-text-dark dark:text-dark-text">
                💡 What makes a great description?
              </span>
              {showGuidance ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
            {showGuidance && (
              <div className="p-4 space-y-3 text-sm text-text-gray dark:text-dark-text-secondary">
                <div>
                  <strong className="text-text-dark dark:text-dark-text">Primary function:</strong>
                  <p>What is the main task this skill performs?</p>
                </div>
                <div>
                  <strong className="text-text-dark dark:text-dark-text">Ideal capabilities:</strong>
                  <p>What can users accomplish with this skill?</p>
                </div>
                <div>
                  <strong className="text-text-dark dark:text-dark-text">Key differentiators:</strong>
                  <p>What makes this skill unique or special?</p>
                </div>
              </div>
            )}
          </div>

          {/* Description - NOW AFTER GUIDANCE */}
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <Label htmlFor="description" className="flex items-center gap-1">
                Description <span className="text-red-500">*</span>
              </Label>
              <div className="flex gap-2">
                {hasEnhanced && (
                  <Button
                    type="button"
                    onClick={handleRevertToOriginal}
                    size="sm"
                    variant="outline"
                    className="rounded-xl"
                  >
                    <RotateCcw className="mr-2 h-3 w-3" />
                    Revert to Original
                  </Button>
                )}
                {formData.description.length >= 50 && !hasEnhanced && (
                  <Button
                    type="button"
                    onClick={handleEnhanceDescription}
                    disabled={enhancing}
                    size="sm"
                    variant="outline"
                    className="rounded-xl border-gray-300 dark:border-[#2e2e2d] text-gray-600 dark:text-gray-400 hover:border-gray-400 dark:hover:border-[#3a3a39] hover:text-gray-700 dark:hover:text-gray-300"
                  >
                    {enhancing ? (
                      <>
                        <Loader className="mr-2 h-3 w-3 animate-spin" />
                        Enhancing...
                      </>
                    ) : (
                      <>
                        <Sparkles className="mr-2 h-3 w-3" />
                        Enhance with AI
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
            <Textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Describe what your skill does and what it's useful for..."
              rows={6}
            />
          </div>

          {/* Category */}
          <div className="space-y-2">
            <Label htmlFor="category_id" className="flex items-center gap-2">
              Category <span className="text-red-500">*</span>
              {detectingCategory && <Loader className="h-3 w-3 animate-spin text-primary" />}
            </Label>

            {/* Suggested Categories */}
            {suggestedCategories.length > 0 && !formData.category_id && (
              <div className="p-3 bg-orange-50 dark:bg-dark-bg-tertiary border border-orange-200 dark:border-[#2e2e2d] rounded-xl">
                <p className="text-sm font-medium text-orange-900 dark:text-orange-300 mb-2">
                  💡 Suggested category:
                </p>
                {suggestedCategories.map(catId => {
                  const cat = categories.find(c => c.value === catId);
                  return cat ? (
                    <button
                      key={catId}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, category_id: catId }))}
                      className="px-3 py-1 bg-orange-100 dark:bg-dark-bg-secondary text-orange-900 dark:text-orange-300 rounded-lg hover:bg-orange-200 dark:hover:bg-dark-bg-hover transition-colors text-sm"
                    >
                      {cat.label}
                    </button>
                  ) : null;
                })}
              </div>
            )}

            <select
              id="category_id"
              name="category_id"
              value={formData.category_id}
              onChange={handleChange}
              className="w-full px-4 py-2 rounded-xl border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg-secondary text-text-dark dark:text-dark-text focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">Select a category...</option>
              <optgroup label="Popular & Accessible">
                {categories.filter(c => c.group === 'Popular').map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </optgroup>
              <optgroup label="Creative & Power Tools">
                {categories.filter(c => c.group === 'Creative').map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </optgroup>
              <optgroup label="Data & Enterprise">
                {categories.filter(c => c.group === 'Data').map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </optgroup>
              <optgroup label="Advanced & Specialized">
                {categories.filter(c => c.group === 'Advanced').map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </optgroup>
            </select>
          </div>
        </div>
      </div>

      {/* Comparison Modal */}
      {showComparison && (
        <ComparisonModal
          original={originalDescription || formData.description}
          enhanced={enhancedDescription}
          onUseOriginal={handleUseOriginal}
          onUseEnhanced={handleUseEnhanced}
          onEditEnhanced={handleEditEnhanced}
          onClose={() => setShowComparison(false)}
        />
      )}
    </WizardLayout>
  );
};

export default Step1PurposeEnhanced;
